import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';
import { AUTHORS } from 'constants/articles/authors';

import HeaderImage from 'assets/resources/using-synthetic-data-from-AILiveSim/HeaderImage.webp';
import ReferenceDataImage from 'assets/resources/using-synthetic-data-from-AILiveSim/reference-data-image.webp';
import ReferenceDataImageTwo from 'assets/resources/using-synthetic-data-from-AILiveSim/reference-data-image-two.webp';
import SyntheticDataImage from 'assets/resources/using-synthetic-data-from-AILiveSim/synthetic-data-image-one.webp';
import SyntheticDataImageTwo from 'assets/resources/using-synthetic-data-from-AILiveSim/synthetic-data-image-two.webp';
import SyntheticDataImageThree from 'assets/resources/using-synthetic-data-from-AILiveSim/synthetic-data-image-three.webp';
import BaselineWithoutAdaptationImage from 'assets/resources/using-synthetic-data-from-AILiveSim/baseline-without-adaptation-image.webp';
import ResultsSummaryImage from 'assets/resources/using-synthetic-data-from-AILiveSim/results-summary-image.webp';

const commonData = {
  ...AUTHORS['Tristan Klempka'],
  title: 'Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models',
  publishDate: 'November 28, 2025',
  minutesOfRead: 20,
};

export const usingSyntheticDataFromAILiveSimMeta: PageMeta = {
  url: '/whitepapers/using-synthetic-data-from-ailivesim',
  title: commonData.title,
  description: 'Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models',
};

export const usingSyntheticDataFromAILiveSimData: ArticleSections[] = [
  {
    resourceType: 'White Paper',
    text: (
      <>
        <p>
          Building reliable computer-vision systems for maritime environments remains a difficult
          challenge. Real data is expensive and time-consuming to collect, and even when available
          it often fails to cover the wide range of rare or hazardous scenarios that autonomous
          systems must handle. In contrast, synthetic data can be generated at scale, designed to
          expose models to difficult edge cases, and systematically varied to explore environmental
          conditions that are nearly impossible to capture in the real world.
        </p>
        <p>
          This first article outlines how we leveraged AILiveSim simulation solution to generate
          synthetic maritime data and how it can be used to significantly improve object-detection
          performance.
        </p>
      </>
    ),
    image: HeaderImage,
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    ...commonData,
  },

  {
    type: 'common',
    title: 'Real-World Reference Data',
    image: ReferenceDataImage,
    text: (
      <>
        <img
          src={ReferenceDataImageTwo}
          alt="dsei-uk"
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            display: 'block',
            marginRight: '16px',
          }}
        />
        <p>
          For real-world evaluation we use an enhanced version of the Singapore Maritime Dataset
          (SMD)1 <strong>[1]</strong>, which we refer to as SMD-Plus2 <strong>[2]</strong>. It
          builds upon the original SMD by providing cleaner labels and more consistent class
          definitions. The complete dataset contains around 4400 labeled fra
        </p>
        <p>
          In the experimental setup described below, roughly 1 100 images are used for the test set.
          We also use a reduced subset of around 2 500 images as a training split in some
          experiments to better compare how additional synthetic samples affect performance.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Generating Synthetic Data with AILiveSim',
    text: (
      <>
        <p>
          The synthetic dataset is generated using AILiveSim’s scenario-based workflow, which makes
          it easy to create many variations of a maritime scene. Parameters such as ship types,
          camera motion, sea-state, lighting, and atmospheric conditions including fog, haze and sun
          angle can be adjusted efficiently. This process naturally supports Domain Randomization 3
          <strong>[3]</strong>, which is essential for exposing models to a wide range of visual
          conditions and improving their ability to generalize to real imagery.
        </p>
        <p>
          Using this approach, we created 24 variations of a base maritime scenario that span
          different viewpoints and environmental conditions while remaining visually consistent with
          the real data used for evaluation. AILiveSim’s automated capture pipeline then generated
          12 000 synthetic frames with consistent metadata, temporal structure, and ready-to-use
          bounding box annotations.
        </p>
        <p>
          This allowed the dataset to be integrated directly into the training pipeline without
          additional processing. As with real data, synthetic frames are split by sequence to avoid
          data leakage.
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '25px',
          }}
        >
          <img
            src={SyntheticDataImage}
            alt="dsei-uk"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              display: 'block',
            }}
          />
          <img
            src={SyntheticDataImageTwo}
            alt="dsei-uk"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              display: 'block',
            }}
          />
          <img
            src={SyntheticDataImageThree}
            alt="dsei-uk"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              display: 'block',
            }}
          />
          <blockquote>
            <p style={{ fontSize: '16px', lineHeight: '120%', marginTop: '-30px' }}>
              Examples of synthetic frames generated with AILiveSim simulator AILiveSim Oy
              Lapinlahdenkatu 1 C, 00180 Helsinki, Finland Confidential 02
            </p>
          </blockquote>
        </div>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Object Detection Model and Evaluation',
    text: (
      <>
        <p>
          The target task is single-class vessel detection. To remain compatible with a large-scale
          pre-trained detector, all ship categories—whether tanker, tugboat, sailboat, or
          speedboat—are treated as a single class. Other objects such as buoys or shore structures
          are ignored.
        </p>
        <p>
          We use YOLOv11n4<strong>[4]</strong>, a modern lightweight detector, trained with standard
          hyper-parameters. The evaluation relies on classical object- detection metrics: F1@50 and
          mAP@50 measure classification and detection accuracy, while mAP@50-95, which averages over
          multiple IoU thresholds, emphasizes bounding-box precision.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Baseline Without Adaptation',
    text: (
      <>
        <p>
          A natural first baseline is to evaluate the off-the-shelf COCO-pretrained 5[5] detector
          directly, without any domain adaptation. Despite being trained on general scenes far
          removed from maritime imagery, YOLOv11n performs surprisingly well on the real SMD-PLUS
          test set. This shows that modern pretrained detectors capture robust visual features
          useful across domains.
        </p>
        <p>
          This baseline is useful because it provides a neutral reference for the experiments that
          follow.
        </p>
        <p>
          By observing how a COCO-pretrained model performs without any adaptation, we can better
          understand the impact of adding real data, synthetic data or both in the later experiment
          configurations.
        </p>
        <img
          src={BaselineWithoutAdaptationImage}
          alt="dsei-uk"
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            display: 'block',
          }}
        />

        <p>
          <strong>On real test data:</strong>
        </p>
        <ul>
          <li>
            F1@50 = <strong>0.95</strong>
          </li>
          <li>
            mAP@50 = <strong>0.96</strong>
          </li>
          <li>
            mAP@50-95 = <strong>0.67</strong>
          </li>
        </ul>
      </>
    ),
  },
  {
    type: 'common',
    title: 'Attempted Domain Adaptation with Limited Real Data',
    text: (
      <>
        <p>
          This experiment setup reflects a very common real-world situation, where a user has only a
          small amount of hard- to-obtain domain-specific data and needs to adapt a generic
          pre-trained network to a specific operational setting.
        </p>
        <p>
          We fine-tuned the COCO-pretrained model on 2 500 real maritime images. Several strategies
          were tested, including freezing different parts of the network and full fine-tuning. All
          produced similar results, and performance was sometimes slightly below the off-the-shelf
          model. This is a typical domain-adaptation 6 [6] limitation. The dataset is too small and
          not diverse enough to shift the COCO feature space toward the maritime domain. Fine-tuning
          modifies robust generic features without providing enough new variation in viewpoint,
          distance or environmental conditions to form a stable maritime representation. The model
          loses useful priors without gaining sufficient domain-specific features.
        </p>
        <p>
          Moreover, this experiment also required the most effort in terms of repeated runs, while
          the synthetic-data experiments that followed delivered clear improvements within a few
          hours and with minimal tuning.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Synthetic-Augmented Low-Data Training',
    text: (
      <>
        <p>
          The low-data regime experiment introduces synthetic data into the training process. By
          combining the same 2 500 real images with an additional 5 000 simulated ones, we observe a
          clear jump in performance, both on accuracy and on bounding- box precision.
        </p>
        <p>
          This model not only detects vessels more reliably but also shows fewer artifacts in
          challenging frames. It remains confident when ships are distant, partially occluded, or
          recorded under difficult lighting or fog. Synthetic data effectively acts as a structural
          regularizer: by covering a broader range of shapes, distances, and visual contexts, it
          prevents the model from drifting toward narrow, over-specialized representations.
        </p>
        <p>
          <strong>On real test data:</strong>
        </p>
        <ul>
          <li>
            F1@50 = <strong>0.98</strong>
          </li>
          <li>
            mAP@50 = <strong>0.99</strong>
          </li>
          <li>
            mAP@50-95 = <strong>0.70</strong>
          </li>
        </ul>
        <p>
          In qualitative comparisons, the mixed- regime model produces cleaner detections and fewer
          false positives. The improvement is also visible in the metric that is hardest to
          influence— mAP@50-95—which reflects improved geometric consistency.
        </p>
      </>
    ),
  },
  {
    type: 'common',
    title: 'Fully Synthetic Training from Scratch',
    text: (
      <>
        <p>
          Finally, we explore a scenario where neither COCO pretraining nor real data is available.
          Training the detector from scratch on 7 500 synthetic images still yields surprisingly
          competitive performance on the real test set. This result is significant: it indicates
          that scenario diversity can compensate, at least partially, for the absence of real-world
          data. Even though synthetic images do not perfectly match real maritime footage, they
          preserve key geometric structures, silhouettes, and motion cues. When the simulator is
          configured carefully, these features alone are enough for a model to learn meaningful
          detectors that transfer to real environments.
        </p>
        <p>Side by side comparaison of detection performance on test sequences.</p>
        <p>(Left) Predictions from the COCO- pretrained model.</p>
        <p>(Right) Predictions from the syntetic- augmented model.</p>

        <p>
          <a
            href="https://drive.google.com/file/d/1T9FNa6ZIhyepv7wWAVvZMYKNEaSHa0UO/view"
            target="_blank"
            rel="noopener noreferrer"
          >
            pretrained_smdplus_results.mp4
          </a>
        </p>
        <p>
          <a
            href="https://drive.google.com/file/d/1C8lf1EyBztUE5RZO6Fx8sjQl3hVFA3Jp/view"
            target="_blank"
            rel="noopener noreferrer"
          >
            Low_regime_smdplus_results.mp4
          </a>
        </p>
        <p
          style={{
            color: '#9CA8B8',
            fontStyle: 'italic',
            textAlign: 'left',
          }}
        >
          Click on the link and on top left arrow to download video. The quality will be much
          better.
        </p>

        <p>
          <strong>On real test data:</strong>
        </p>
        <ul>
          <li>
            F1@50 = <strong>0.93</strong>
          </li>
          <li>
            mAP@50 = <strong>0.96</strong>
          </li>
          <li>
            mAP@50-95 = <strong>0.55</strong>
          </li>
        </ul>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Results Summary',
    image: ResultsSummaryImage,
  },

  {
    type: 'common',
    title: 'Conclusion',
    text: (
      <>
        <p>
          These experiments highlight the value of AILiveSim synthetic data for training maritime
          object-detection models. In both mixed and synthetic-only setups, the simulator provides
          enough visual diversity and scenario coverage to achieve strong performance on real test
          footage. The synthetic sequences capture the structures, motions and environmental
          variations that are costly or difficult to obtain at sea, delivered in a controlled and
          scalable way. This makes AILiveSim an effective solution for improving model performance
          when real maritime data is limited or unavailable.
        </p>
        <p>
          A standalone archive containing the trained models for the low-data regime and
          bootstrapped experiments with the data used in this study are provided to facilitate
          reproducibility of the results.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: "Want to know more about AILiveSim?",
    text: (
      <>
        <p>
          <strong>Do you have questions or want to hear more about the research?</strong>
        </p>
        <p>Please send your questions to: <a href="mailto:info@ailivesim.com">info@ailivesim.com</a></p>
        <p>Discuss with our experts: <a href="https://meetings.hubspot.com/mharalson?uuid=7c84a609-86db-48d0-9bd4-4eaf0447e15f" target="_blank" rel="noopener noreferrer">Book a meeting with us</a></p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Stay Up to date',
    text: (
      <>
        <p>
          <strong>Want to know about our new white papers and articles?</strong>
        </p>
        <p>
          <strong><a href="https://ailivesim.com/resources" target="_blank" rel="noopener noreferrer">Subscribe to AILiveSim newsletter</a></strong>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Glossary:',
    text: (
      <>
        <ul>
          <li>
            <strong>COCO-pretrained model:</strong> A model trained on the COCO dataset (Common
            Objects in Context), a widely used benchmark for object detection, segmentation, and
            keypoint detection.
          </li>
          <li>
            <strong>YOLOv11n:</strong> A lightweight variant of the YOLOv11 object-detection
            architecture (“n” for nano ), optimized for fast inference and low computational cost
            while maintaining solid detection performance.
          </li>
          <li>
            <strong>F1@50:</strong> The F1 score (harmonic mean of precision and recall) calculated
            at an IoU threshold of 0.5. It measures how well objects are detected and correctly
            classifi ed.
          </li>
          <li>
            <strong>mAP@50:</strong> Mean Average Precision computed at a single IoU threshold of
            0.5. It refl ects overall detection accuracy across all object classes.
          </li>
        </ul>
      </>
    ),
  },

  {
    type: 'common',
    title: 'References',
    text: (
      <>
        <p>
          [1] Prasad, D. K., Prasath, C. K., Rajan, D., Rachmawati, L., Rajabally, E., & Quek, C.
          (2018). Object detection in a maritime environment: Performance evaluation of background
          subtraction methods. IEEE Transactions on Intelligent Transportation Systems, 20(5),
          1787-1802
        </p>
        <p>
          [2] Kim, J. H., Kim, N., Park, Y. W., & Won, C. S. (2022). Object detection and classifi
          cation based on YOLO-V5 with improved maritime dataset. Journal of Marine Science and
          Engineering, 10(3), 377.
        </p>
        <p>
          [3] Tobin, J., Fong, R., Ray, A., Schneider, J., Zaremba, W., & Abbeel, P. (2017,
          September). Domain randomization for transferring deep neural networks from simulation to
          the real world. In 2017 IEEE/RSJ international conference on intelligent robots and
          systems (IROS) (pp. 23-30). IEEE.
        </p>
        <p>
          [4] Khanam, R., & Hussain, M. (2024). Yolov11: An overview of the key architectural
          enhancements. arXiv preprint arXiv:2410.17725.
        </p>
        <p>
          [5] Lin, T. Y., Maire, M., Belongie, S., Hays, J., Perona, P., Ramanan, D., ... & Zitnick,
          C. L. (2014, September). Microsoft coco: Common objects in context. In European conference
          on computer vision (pp. 740-755). Cham: Springer International Publishing.
        </p>
        <p>
          [6] Farahani, A., Voghoei, S., Rasheed, K., & Arabnia, H. R. (2021). A brief review of
          domain adaptation. Advances in data science and information engineering: proceedings from
          ICDATA 2020 and IKE 2020, 877-894.
        </p>
      </>
    ),
  }
];

export const usingSyntheticDataFromAILiveSimCard: TCardProps = {
  description: 'This describes how AILiveSim synthetic data trains robust maritime object-detection models when real sea data is expensive or unavailable. Covers generating 12,000 annotated frames with domain randomization, achieving F1@50 of 0.93 and mAP@50 of 0.96 on real test footage. Synthetic scenarios safely expose models to rare maritime conditions impossible to capture at sea.',
  image: HeaderImage,
  type: 'White Papers',
  category: 'whitepapers',
  postUrl: usingSyntheticDataFromAILiveSimMeta.url,
  ...commonData,
  hashTags: [EHashtag.AutonomousVehicles, EHashtag.SyntheticData, EHashtag.AISimulation],
};
